import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Toaster, toast } from "sonner";
import {
  Boxes,
  ArrowLeft,
  Calendar,
  CalendarClock,
  Compass,
  FileText,
  Globe,
  Home,
  LifeBuoy,
  LogOut,
  Menu,
  Music,
  Palette,
  RefreshCw,
  Search,
  Settings,
  Shield,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";
import { listFestivals } from "@/api/client";
import { TourCoach } from "@/components/product-tour/TourCoach";
import { BrandActions } from "@/components/shell/BrandActions";
import { CommandPalette } from "@/components/shell/CommandPalette";
import { IOSInstallBanner } from "@/components/IOSInstallBanner";
import { Icon } from "@/components/ds";
import { OrgBanner } from "@/components/OrgBanner";
import { OrgSwitcher } from "@/components/shell/OrgSwitcher";
import { SupportDialog } from "@/components/support/SupportDialog";
import { UpdateBanner } from "@/components/UpdateBanner";
import { TierBadge } from "@/components/GatedFeature";
import { useAuthStore } from "@/stores/auth";
import { usePreferencesStore } from "@/stores/preferences";
import { useSupportStore } from "@/stores/support";
import { cn } from "@/lib/utils";
import { initialsFor } from "@/lib/orgColor";
import {
  isMobileProductionRoute,
  mobileFestivalIdFromPath,
  mobileProductionHref,
  mobileRecentFestivalKey,
} from "@/lib/mobileFestivalContext";
import { hasInAppHistory, mobileBackFallback } from "@/lib/mobileNavigation";
import { shouldShowOrgBanner } from "@/lib/orgBannerVisibility";

type Feature = "upload_rider" | "crew_calculator";

type MobileDockItem =
  | {
      kind: "link";
      to: string;
      label: string;
      icon: LucideIcon;
      active: boolean;
    }
  | {
      kind: "action";
      label: string;
      icon: LucideIcon;
      active: boolean;
      onSelect: () => void;
    };

const artistNavItems = [{ to: "/my-schedule", label: "My Schedule", icon: Music }];

const drawerProductionItems: {
  to: string;
  label: string;
  icon: LucideIcon;
  feature?: Feature;
}[] = [
  { to: "/dashboard", label: "Dashboard", icon: Home },
  { to: "/festivals", label: "Festivals", icon: Calendar },
  { to: "/riders", label: "Riders", icon: FileText, feature: "upload_rider" },
  { to: "/crew", label: "Crew", icon: Users, feature: "crew_calculator" },
];

function MobileNavLink({
  to,
  label,
  icon,
  active,
  trailing,
  onNavigate,
}: {
  to: string;
  label: string;
  icon: LucideIcon;
  active?: boolean;
  trailing?: ReactNode;
  onNavigate?: () => void;
}) {
  return (
    <Link
      to={to}
      onClick={onNavigate}
      className={cn("mobile-drawer-link", active && "mobile-drawer-link--active")}
    >
      <span className="mobile-drawer-link__icon">
        <Icon icon={icon} size={18} />
      </span>
      <span className="mobile-drawer-link__label">{label}</span>
      {trailing}
    </Link>
  );
}

export function MobileAppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, logout, activeOrganizationId } = useAuthStore();
  const { hideProduction } = usePreferencesStore();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const openSupport = useSupportStore((s) => s.open);
  const [searchOpen, setSearchOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [openOrgSwitcherOnDrawer, setOpenOrgSwitcherOnDrawer] = useState(false);

  const currentFestivalId = mobileFestivalIdFromPath(location.pathname);
  const festivalMemoryKey = mobileRecentFestivalKey(activeOrganizationId);
  const [recentFestivalContext, setRecentFestivalContext] = useState(() => ({
    key: festivalMemoryKey,
    id: localStorage.getItem(festivalMemoryKey),
  }));
  const recentFestivalId =
    recentFestivalContext.key === festivalMemoryKey
      ? recentFestivalContext.id
      : null;
  const backFallback = mobileBackFallback(location.pathname);
  const { data: festivals } = useQuery({
    queryKey: ["festivals", activeOrganizationId],
    queryFn: () => listFestivals(activeOrganizationId),
    staleTime: 60_000,
  });
  const availableFestivalIds = useMemo(
    () => festivals?.map((festival) => festival.id),
    [festivals],
  );

  const productionHref = useMemo(() => {
    return mobileProductionHref({
      currentFestivalId,
      recentFestivalId,
      availableFestivalIds,
    });
  }, [availableFestivalIds, currentFestivalId, recentFestivalId]);

  useEffect(() => {
    setRecentFestivalContext({
      key: festivalMemoryKey,
      id: localStorage.getItem(festivalMemoryKey),
    });
  }, [festivalMemoryKey]);

  useEffect(() => {
    if (!currentFestivalId || !availableFestivalIds?.includes(currentFestivalId)) return;
    localStorage.setItem(festivalMemoryKey, currentFestivalId);
    setRecentFestivalContext({ key: festivalMemoryKey, id: currentFestivalId });
  }, [availableFestivalIds, currentFestivalId, festivalMemoryKey]);

  useEffect(() => {
    if (!availableFestivalIds || !recentFestivalId) return;
    if (availableFestivalIds.includes(recentFestivalId)) return;
    localStorage.removeItem(festivalMemoryKey);
    setRecentFestivalContext({ key: festivalMemoryKey, id: null });
  }, [availableFestivalIds, festivalMemoryKey, recentFestivalId]);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!drawerOpen || !openOrgSwitcherOnDrawer) return;
    const timer = window.setTimeout(() => {
      document
        .querySelector<HTMLButtonElement>(".mobile-drawer [data-org-switcher]")
        ?.click();
      setOpenOrgSwitcherOnDrawer(false);
    }, 240);
    return () => window.clearTimeout(timer);
  }, [drawerOpen, openOrgSwitcherOnDrawer]);

  const isActive = (to: string, items: { to: string }[] = []) => {
    const matches = location.pathname === to || location.pathname.startsWith(to + "/");
    if (!matches) return false;
    const moreSpecific = items.some(
      (n) =>
        n.to !== to &&
        n.to.startsWith(to + "/") &&
        (location.pathname === n.to || location.pathname.startsWith(n.to + "/")),
    );
    return !moreSpecific;
  };

  const isProductionActive = isMobileProductionRoute(
    location.pathname,
    location.search,
  );

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleBack = () => {
    if (!backFallback) return;
    if (hasInAppHistory(window.history.state)) {
      navigate(-1);
      return;
    }
    navigate(backFallback, { replace: true });
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await queryClient.invalidateQueries();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Refresh failed");
    } finally {
      setTimeout(() => setIsRefreshing(false), 600);
    }
  };

  const bottomItems: MobileDockItem[] = [
    {
      kind: "link",
      to: "/festivals",
      label: "Festivals",
      icon: Calendar,
      active: location.pathname.startsWith("/festivals") && !isProductionActive,
    },
    {
      kind: "link",
      to: "/riders",
      label: "Riders",
      icon: FileText,
      active: location.pathname.startsWith("/riders"),
    },
    {
      kind: "link",
      to: productionHref,
      label: "Production",
      icon: Boxes,
      active: isProductionActive || location.pathname === "/crew",
    },
    {
      kind: "action",
      label: "Search",
      icon: Search,
      active: searchOpen,
      onSelect: () => setSearchOpen(true),
    },
    {
      kind: "link",
      to: "/settings",
      label: "Settings",
      icon: Settings,
      active: location.pathname.startsWith("/settings"),
    },
  ];

  return (
    <div className="mobile-app bg-bg text-ink md:hidden">
      <header className="mobile-topbar">
        <div className="mobile-topbar__row">
          <div className="flex shrink-0 items-center gap-0.5">
            {backFallback && (
              <button
                type="button"
                className="mobile-iconbtn"
                onClick={handleBack}
                aria-label="Back"
                title="Back"
              >
                <Icon icon={ArrowLeft} size={20} />
              </button>
            )}
            <button
              type="button"
              className="mobile-iconbtn"
              onClick={() => setDrawerOpen(true)}
              aria-label="Open mobile menu"
              title="Menu"
            >
              <Icon icon={Menu} size={20} />
            </button>
          </div>

          <Link to="/dashboard" className="mobile-brand" aria-label="Dashboard">
            <img
              src="/trace-logo.svg"
              alt="TRACE"
              className="h-[17px]"
              style={{ filter: "var(--logo-invert)" }}
            />
          </Link>

          <div className="mobile-topbar__actions">
            <Link to="/learn" className="mobile-iconbtn" aria-label="Explore Trace" title="Explore Trace"><Icon icon={Compass} size={19} /></Link>
            <button
              type="button"
              className="mobile-iconbtn"
              onClick={handleRefresh}
              disabled={isRefreshing}
              aria-label="Refresh"
            >
              <Icon icon={RefreshCw} size={19} className={isRefreshing ? "animate-spin" : ""} />
            </button>
          </div>
        </div>
      </header>

      <UpdateBanner />
      <TourCoach />

      <main className="mobile-main ds-scroll">
        <div className="mobile-main__inner">
          {shouldShowOrgBanner(location.pathname) && (
            <OrgBanner
              onSwitchOrg={() => {
                setOpenOrgSwitcherOnDrawer(true);
                setDrawerOpen(true);
              }}
            />
          )}
          <Outlet />
        </div>
      </main>

      <nav className="mobile-bottom-nav" aria-label="Primary navigation">
        <div className="mobile-bottom-nav__dock">
          {bottomItems.map((item) => {
            const contents = (
              <>
                <span className="mobile-bottom-link__icon" aria-hidden="true">
                  <Icon icon={item.icon} size={22} />
                </span>
              </>
            );
            const className = cn(
              "mobile-bottom-link",
              item.active && "mobile-bottom-link--active",
            );

            return item.kind === "link" ? (
              <Link
                key={item.label}
                to={item.to}
                replace={item.active}
                className={className}
                aria-label={item.label}
                aria-current={item.active ? "page" : undefined}
              >
                {contents}
              </Link>
            ) : (
              <button
                key={item.label}
                type="button"
                onClick={item.onSelect}
                className={className}
                aria-label={item.label}
                aria-haspopup="dialog"
                aria-expanded={item.active}
                aria-controls="global-search-dialog"
              >
                {contents}
              </button>
            );
          })}
        </div>
      </nav>

      <div
        className={cn("mobile-scrim", drawerOpen && "mobile-scrim--open")}
        aria-hidden="true"
        onClick={() => setDrawerOpen(false)}
      />
      <aside
        className={cn("mobile-drawer", drawerOpen && "mobile-drawer--open")}
        aria-hidden={!drawerOpen}
        aria-label="Mobile menu"
      >
        <div className="mobile-drawer__head">
          <div>
            <img
              src="/trace-logo.svg"
              alt="TRACE"
              className="h-[18px]"
              style={{ filter: "var(--logo-invert)" }}
            />
            <p className="brand__tag mt-1.5">Festival Intelligence</p>
          </div>
          <div className="flex items-center gap-1">
            <BrandActions />
            <button
              type="button"
              className="mobile-iconbtn"
              onClick={() => setDrawerOpen(false)}
              aria-label="Close mobile menu"
            >
              <Icon icon={X} size={19} />
            </button>
          </div>
        </div>

        <div className="mobile-drawer__org">
          <OrgSwitcher />
        </div>

        <nav className="mobile-drawer__nav" aria-label="All navigation">
          {user?.has_artist_profile && (
            <div className="mobile-drawer-group">
              <p className="mobile-drawer-group__label">Artist</p>
              {artistNavItems.map((item) => (
                <MobileNavLink
                  key={item.to}
                  to={item.to}
                  label={item.label}
                  icon={item.icon}
                  active={isActive(item.to)}
                />
              ))}
            </div>
          )}

          {!(hideProduction && user?.has_artist_profile) && (
            <div className="mobile-drawer-group">
              <p className="mobile-drawer-group__label">Production</p>
              {drawerProductionItems.map((item) => (
                <MobileNavLink
                  key={item.to}
                  to={item.to}
                  label={item.label}
                  icon={item.icon}
                  active={isActive(item.to, drawerProductionItems)}
                  trailing={item.feature ? <TierBadge feature={item.feature} /> : undefined}
                />
              ))}
            </div>
          )}

          <div className="mobile-drawer-group">
            <p className="mobile-drawer-group__label">More</p>
            <MobileNavLink to="/learn" label="Explore Trace" icon={Compass} active={isActive("/learn")} />
            <MobileNavLink to="/live" label="Discover" icon={Globe} />
            <MobileNavLink to="/my-shifts" label="My Shifts" icon={CalendarClock} active={isActive("/my-shifts")} />
            {user?.is_superadmin && (
              <MobileNavLink to="/design-system" label="Design System" icon={Palette} active={isActive("/design-system")} />
            )}
            <button
              type="button"
              className="mobile-drawer-link w-full text-left"
              onClick={() => {
                // The drawer only auto-closes on route change; Help is not a route.
                setDrawerOpen(false);
                openSupport();
              }}
              aria-haspopup="dialog"
            >
              <span className="mobile-drawer-link__icon">
                <Icon icon={LifeBuoy} size={18} />
              </span>
              <span className="mobile-drawer-link__label">Help</span>
            </button>
            {user?.is_superadmin && (
              <MobileNavLink to="/admin" label="Admin" icon={Shield} active={isActive("/admin")} />
            )}
          </div>
        </nav>

        <div className="mobile-drawer__user">
          <span className="grid h-10 w-10 place-items-center rounded-[10px] bg-panel-3 text-[12px] font-bold text-ink-2">
            {initialsFor(user?.name || user?.email)}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[13px] font-semibold text-ink">
              {user?.name || user?.email || "User"}
            </p>
            <p className="truncate text-[11px] capitalize text-ink-3">
              {user?.effective_tier || user?.subscription_status}
            </p>
          </div>
          <button type="button" className="mobile-iconbtn" onClick={handleLogout} aria-label="Log out">
            <Icon icon={LogOut} size={18} />
          </button>
        </div>
      </aside>

      <CommandPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
      <SupportDialog />

      <IOSInstallBanner />
      <Toaster
        theme="dark"
        position="bottom-center"
        offset={{
          left: 12,
          right: 12,
          bottom: "var(--tr-mobile-dock-clearance)",
        }}
        mobileOffset={{
          left: 12,
          right: 12,
          bottom: "var(--tr-mobile-dock-clearance)",
        }}
      />
    </div>
  );
}
